// src/pages/CruscottoScadenzePage.js
// Un solo posto per tutto ciò che ha una scadenza (o rischia di diventarne una) — oggi sparso
// in tre moduli diversi: Scadenzario, Manutenzione, e i SAL fermi da tempo. Nessun dato nuovo:
// mette solo in fila quello che già esiste in Nexiora.
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { Clock, AlertTriangle } from "lucide-react";
import { FREQUENZE, prossimaScadenza } from "../components/ManutenzioneTab";

function giorniDa(data) {
  const d = data instanceof Date ? data : new Date(data);
  return Math.round((d - new Date()) / 86400000);
}
function dataItaliana(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString("it-IT");
}

export default function CruscottoScadenzePage({ onNavigate }) {
  const { effectiveOrgId: orgId } = useOrg();
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("scadenziario");
  const [scadenzarioItems, setScadenzarioItems] = useState([]);
  const [manutenzioneItems, setManutenzioneItems] = useState([]);
  const [salItems, setSalItems] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const scadSnap = await getDocs(query(collection(db,"scadenzario"), where("organizationId","==",orgId)));
      const scad = scadSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(s => s.dataScadenza && !s.completata)
        .map(s => ({ tipo:"scadenzario", titolo:s.nome||s.descrizione||"Scadenza", dettaglio:s.targa||s.descrizione||"", data:new Date(s.dataScadenza+"T00:00:00") }));

      const [impiantiSnap, pianiSnap] = await Promise.all([
        getDocs(query(collection(db,"manutenzioneImpianti"), where("organizationId","==",orgId))),
        getDocs(query(collection(db,"manutenzionePiani"), where("organizationId","==",orgId))),
      ]);
      const impianti = Object.fromEntries(impiantiSnap.docs.map(d=>[d.id,d.data()]));
      const piani = pianiSnap.docs.map(d=>({id:d.id,...d.data()}));
      const manutenzione = await Promise.all(piani.map(async (p) => {
        const intSnap = await getDocs(query(collection(db,"manutenzioneInterventi"), where("pianoId","==",p.id), where("organizationId","==",orgId)));
        const interventi = intSnap.docs.map(d=>d.data()).sort((a,b)=>(b.data||"").localeCompare(a.data||""));
        const scadenza = prossimaScadenza(p, interventi[0]||null);
        const impianto = impianti[p.impiantoId];
        return { tipo:"manutenzione", titolo:p.nome||"Piano manutenzione", dettaglio:impianto?.nome||"", data:scadenza, projectId:impianto?.projectId };
      }));

      const salSnap = await getDocs(query(collection(db,"computiSAL"), where("orgId","==",orgId)));
      const sal = salSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(s => (s.periodi||[]).length>0)
        .map(s => {
          const ultimo = s.periodi[s.periodi.length-1];
          return { tipo:"sal", titolo:s.projectName||"Commessa", dettaglio:s.clienteNome||"", data:new Date(ultimo.data+"T00:00:00"), projectId:s.projectId };
        });

      setScadenzarioItems(scad);
      setManutenzioneItems(manutenzione);
      setSalItems(sal);
      setCaricato(true);
    })();
  }, [orgId]);

  if (!caricato) return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Caricamento...</div>;

  const prossime = [...scadenzarioItems, ...manutenzioneItems]
    .filter(i => giorniDa(i.data) <= 60)
    .sort((a,b)=>a.data-b.data);
  const salFermi = salItems.filter(s => giorniDa(s.data) <= -30).sort((a,b)=>a.data-b.data);

  const Riga = ({ item }) => {
    const gg = giorniDa(item.data);
    const scaduta = gg < 0;
    return (
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderBottom:"1px solid #f3f4f6"}}>
        <div style={{width:8,height:8,borderRadius:"50%",flexShrink:0,background: scaduta?"#dc2626":gg<=7?"#d97706":"#059669"}}/>
        <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:6,background:"#f3f4f6",color:"#374151",flexShrink:0,textTransform:"uppercase"}}>
          {item.tipo==="scadenzario"?"Scadenzario":"Manutenzione"}
        </span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12.5,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.titolo}</div>
          {item.dettaglio && <div style={{fontSize:11,color:"#6b7280"}}>{item.dettaglio}</div>}
        </div>
        <div style={{fontSize:11.5,fontWeight:700,color: scaduta?"#dc2626":"#374151",flexShrink:0}}>
          {dataItaliana(item.data)} {scaduta?`(scaduta da ${Math.abs(gg)}gg)`:gg===0?"(oggi)":`(tra ${gg}gg)`}
        </div>
      </div>
    );
  };

  if (!puoAccedere) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🔒</div>
        <div style={{fontSize:15,fontWeight:700,color:"#374151",marginBottom:6}}>Accesso riservato</div>
        <div style={{fontSize:13,color:"#9ca3af",maxWidth:360}}>Questa sezione è visibile solo a Titolare, Gestione aziendale, Amministrazione e Project Manager.</div>
      </div>
    );
  }

  return (
    <div style={{padding:24,maxWidth:900,overflowY:"auto",height:"100%"}}>
      <h2 style={{fontSize:18,fontWeight:800,display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <Clock size={19} color="#7c3aed"/> Cruscotto Scadenze
      </h2>
      <p style={{fontSize:12,color:"#6b7280",marginBottom:18}}>Scadenzario e Manutenzione insieme — niente di nuovo, solo messo in un unico posto.</p>

      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,overflow:"hidden",marginBottom:20}}>
        <div style={{padding:"10px 14px",background:"#faf5ff",fontSize:12.5,fontWeight:700,color:"#5b21b6"}}>Prossime 60 giorni ({prossime.length})</div>
        {prossime.length===0 ? (
          <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>Nessuna scadenza nei prossimi 60 giorni</div>
        ) : prossime.map((it,i)=><Riga key={i} item={it}/>)}
      </div>

      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,overflow:"hidden"}}>
        <div style={{padding:"10px 14px",background:"#fffbeb",fontSize:12.5,fontWeight:700,color:"#92400e",display:"flex",alignItems:"center",gap:6}}>
          <AlertTriangle size={14}/> Commesse con SAL fermo da oltre 30 giorni ({salFermi.length})
        </div>
        <p style={{padding:"8px 14px 0",fontSize:11,color:"#9ca3af"}}>Non è una vera scadenza — è un promemoria: l'ultimo Stato Avanzamento Lavori risale a più di un mese fa.</p>
        {salFermi.length===0 ? (
          <div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:12}}>Nessuna commessa ferma da oltre 30 giorni</div>
        ) : salFermi.map((it,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #f3f4f6",cursor:onNavigate?"pointer":"default"}}
            onClick={()=>onNavigate&&it.projectId&&onNavigate({page:"project",projectId:it.projectId,project:{id:it.projectId,name:it.titolo},initialTab:"sal"})}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12.5,fontWeight:600,color:"#111827"}}>{it.titolo}</div>
              <div style={{fontSize:11,color:"#6b7280"}}>{it.dettaglio}</div>
            </div>
            <div style={{fontSize:11.5,fontWeight:700,color:"#92400e"}}>ultimo SAL: {dataItaliana(it.data)} ({Math.abs(giorniDa(it.data))}gg fa)</div>
          </div>
        ))}
      </div>
    </div>
  );
}
